'use client';

import React, { useEffect, useState, useRef } from "react";
import Image from 'next/image';
import Link from 'next/link';
import axios from "axios";
import { API_BASE_URL, API_UPLOAD_URL, getImageUrl } from '@/app/lib/api';

interface Slide {
  id: string;
  title?: string;
  description?: string; // Utilisé comme URL de redirection
  imageUrl: string;
}

const HeroSliderSkeleton: React.FC = () => (
  <section className="relative w-full aspect-[16/8] sm:aspect-[21/9] md:aspect-auto md:h-[65vh] bg-gray-200 animate-pulse overflow-hidden">
    <div className="absolute inset-0 flex items-center justify-center">
      <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
  </section>
);

const normalizeUrl = (url: string | null): string => {
  if (!url) return "/placeholder.png";
  return getImageUrl(url);
};

export default function Hero() {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const touchStartX = useRef<number | null>(null);

  // --- Fetch Data ---
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const resp = await axios.get(`${API_BASE_URL}/slides`);
        const data = Array.isArray(resp.data) ? resp.data : (resp.data.items || []);
        const transformed = data.map((s: any) => ({
          id: s.id,
          title: s.title,
          description: s.description,
          imageUrl: s.imageUrl,
        })).filter((s: Slide) => s.imageUrl);
        setSlides(transformed);
      } catch (err) {
        console.error("Erreur slides:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSlides();
  }, []);

  // --- Auto Play (Défilement automatique) ---
  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      nextSlide();
    }, 5000); // Change toutes les 5 secondes
    return () => clearInterval(timer);
  }, [currentIndex, slides]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(deltaX) > 50) {
      deltaX > 0 ? prevSlide() : nextSlide();
    }
    touchStartX.current = null;
  };

  if (loading) return <HeroSliderSkeleton />;
  if (slides.length === 0) return null;

  const currentSlide = slides[currentIndex];

  return (
    <section
      className="relative w-full aspect-[16/8] sm:aspect-[21/9] md:aspect-auto md:h-[65vh] overflow-hidden bg-white group select-none"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative w-full h-full">
        {/* L'image seule, sans texte ni dégradé */}
        <Image
          key={currentSlide.id}
          src={normalizeUrl(currentSlide.imageUrl)}
          alt={currentSlide.title || "Bannière"}
          fill
          priority
          unoptimized
          sizes="100vw"
          className="object-cover object-center transition-opacity duration-1000 ease-in-out"
          draggable={false}
        />

        {/* Zone cliquable si une URL est fournie dans la description */}
        {currentSlide.description && (currentSlide.description.startsWith('http') || currentSlide.description.startsWith('/')) && (
          <Link
            href={currentSlide.description}
            target={currentSlide.description.startsWith('http') ? "_blank" : undefined}
            rel={currentSlide.description.startsWith('http') ? "noopener noreferrer" : undefined}
            className="absolute inset-0 z-20"
            aria-label="Lien vers la promotion"
          />
        )}
      </div>

      {/* Flèches de navigation (Desktop) */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-black/10 hover:bg-black/30 text-white opacity-0 group-hover:opacity-100 transition-opacity hidden md:block"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-black/10 hover:bg-black/30 text-white opacity-0 group-hover:opacity-100 transition-opacity hidden md:block"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Indicateurs (Dots) en bas */}
      {slides.length > 1 && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-4 z-30 flex gap-2">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentIndex ? 'w-8 bg-indigo-600' : 'w-2 bg-gray-300 hover:bg-gray-400'
                }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}